import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  triggerGeneration,
  listGenerationJobs,
  processNextTask,
} from "@/lib/generate.functions";
import { toast } from "sonner";
import { formatDate } from "@/lib/site";
import { useRealtimeInvalidate } from "@/hooks/useRealtimeInvalidate";
import { WorkerPanel } from "@/components/admin/WorkerPanel";

export const Route = createFileRoute("/admin/generate")({
  component: GeneratePage,
});

function GeneratePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [count, setCount] = useState(3);

  const { data: jobs, refetch } = useQuery({
    queryKey: ["admin", "jobs"],
    queryFn: () => listGenerationJobs(),
    refetchInterval: 5000,
  });
  useRealtimeInvalidate([
    { table: "generation_jobs", queryKey: ["admin", "jobs"] },
    { table: "article_generation_tasks", queryKey: ["admin", "jobs"] },
  ]);

  const run = useMutation({
    mutationFn: () => triggerGeneration({ data: { count } }),
    onSuccess: (r: any) => {
      toast.success(
        `Queued ${r?.taskCount ?? "?"} tasks. Redirecting to dashboard for live progress…`,
      );
      qc.invalidateQueries({ queryKey: ["admin", "jobs"] });
      qc.invalidateQueries({ queryKey: ["admin", "articles"] });
      qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] });
      refetch();
      setTimeout(() => navigate({ to: "/admin" }), 600);
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to start generation"),
  });

  const processOne = useMutation({
    mutationFn: () => processNextTask(),
    onSuccess: (r: any) => {
      if (r?.status === "idle") toast.info("No pending tasks.");
      else if (r?.outcome === "completed") toast.success(`Drafted: ${r.slug}`);
      else if (r?.outcome === "skipped") toast.message("Task skipped (no usable source).");
      else toast.error("Task failed — will retry.");
      refetch();
    },
    onError: (e: any) => toast.error(e.message ?? "Worker error"),
  });

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
      <section>
        <h1 className="headline-display text-3xl">Quick generate</h1>
        <p className="mt-2 text-muted-foreground text-sm">
          <strong>Shortcut, not the main flow.</strong> Skip the idea backlog and queue N
          articles drafted straight from the top sources. Use <Link to="/admin/ideas" search={{ status: "new" }} className="underline">Ideas</Link> for
          editorial control (review → approve → draft → edit).
        </p>

        <div className="mt-6"><WorkerPanel /></div>

        <div className="mt-8 border border-border p-6 bg-paper-soft">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            How many articles to queue
          </label>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={8}
              value={count}
              onChange={(e) =>
                setCount(Math.min(8, Math.max(1, Number(e.target.value) || 1)))
              }
              className="w-20 border border-border bg-background px-3 py-2"
            />
            <button
              onClick={() => run.mutate()}
              disabled={run.isPending}
              className="bg-foreground text-background px-5 py-2 font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
            >
              {run.isPending ? "Queuing…" : "Queue articles"}
            </button>
            <button
              onClick={() => processOne.mutate()}
              disabled={processOne.isPending}
              className="border border-border px-4 py-2 text-sm hover:bg-paper-soft disabled:opacity-50"
              title="Run the worker once now"
            >
              {processOne.isPending ? "Working…" : "Process next now"}
            </button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Each task takes ~30–90s. The cron worker fires once a minute, or click
            “Process next now” to drain manually.
          </p>
        </div>

        <div className="mt-8 text-sm space-y-2 text-muted-foreground">
          <p className="eyebrow">Source tiers</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>A — Official Google (Search Central, Maps Platform, GBP Help, blog.google/Maps)</li>
            <li>B — Named Google people (Sophia Lin, Thomas Escobar, et al.)</li>
            <li>C — Industry experts (Sterling Sky, Near Media, SE Land, Whitespark, BrightLocal)</li>
            <li>D — Community (Reddit, Local Search Forum)</li>
          </ul>
        </div>
      </section>

      <section>
        <h2 className="headline-display text-2xl">Recent runs</h2>
        <div className="mt-4 divide-y divide-border border border-border">
          {(!jobs || jobs.length === 0) && (
            <div className="p-6 text-sm text-muted-foreground">No runs yet.</div>
          )}
          {jobs?.map((j: any) => {
            const c = j.taskCounts ?? {};
            const progressLabel =
              c.total > 0
                ? `${c.completed}/${c.total} drafted${c.failed ? ` · ${c.failed} failed` : ""}${c.skipped ? ` · ${c.skipped} skipped` : ""}${c.running ? ` · ${c.running} running` : ""}${c.pending ? ` · ${c.pending} pending` : ""}`
                : null;
            return (
              <div key={j.id} className="p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span
                    className={
                      j.status === "completed"
                        ? "text-green-700 font-medium"
                        : j.status === "failed"
                          ? "text-red-700 font-medium"
                          : "text-foreground font-medium"
                    }
                  >
                    {j.status}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(j.created_at)}
                  </span>
                </div>
                {progressLabel && (
                  <p className="mt-1 text-xs text-muted-foreground">{progressLabel}</p>
                )}
                {j.articles_created && j.articles_created.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {j.articles_created.map((a: any) => (
                      <li key={a.slug}>
                        <Link
                          to="/article/$slug"
                          params={{ slug: a.slug }}
                          className="underline hover:text-accent"
                        >
                          {a.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                {j.error_message && (
                  <p className="mt-2 text-xs text-red-700">{j.error_message}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
